import {useEffect, useRef, useState} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setSkipMembership,
  setForceLogin,
  setHasMembershipContext,
  setMemberOnlyTicketsDeleted,
  membershipGoToSignIn,
  membershipSignOut,
  setCheckoutOverlay
} from '@checkout/actions/ui';
import { wrappedFetch } from '@checkout-h/fetch';
import { useTranslation } from '@checkout-c/Translations';
import { membershipLogoutAndRefresh } from '../helpers/membershipLogoutAndRefresh';
import { selectIsBannerLayout, selectIsHeroLayout, selectIsPosterLayout} from '@checkout-b/checkout/selectors/ui';
import { navigate } from '@checkout-b/checkout/helpers/navigationService';
import clsx from 'clsx';

const MemberAuthButtons = () => {
  const rootRef = useRef(null);
  const dispatch = useDispatch();

  const isMembershipAvailable = !!useSelector(state => state.ui?.uiConfig?.membershipFormConfig?.membership_available);
  const isMembershipRequired = !!useSelector(state => state.ui?.uiConfig?.membershipFormConfig?.membership_required);
  const isMembershipSkipped = !!useSelector(state => state.ui?.skipMembership);
  const isMemberLoggedIn = useSelector(state => state.ui.hasMembershipContext);
  const isHeroLayout = useSelector(selectIsHeroLayout);
  const isBannerLayout = useSelector(selectIsBannerLayout);
  const isPosterLayout = useSelector(selectIsPosterLayout);
  const isBannerOrPoster = isBannerLayout || isPosterLayout;
  const isSkipLogin = useSelector(state => state.ui?.uiConfig?.membershipFormConfig?.skip_login);
  const token = useSelector(state => state.reservation?.token);
  const secret = useSelector(state => state.reservation?.secret);
  const selectedChildEvent = useSelector(state => state.event?.childEvent?.event);
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const isRecurring = useSelector(state => state.ui.isRecurring);

  useEffect(() => {
    if (!isMembershipAvailable) return;
    const membershipGating = isMembershipRequired || !isSkipLogin;
    if (isBannerOrPoster && membershipGating && !isMemberLoggedIn && (!isMembershipSkipped || isMembershipRequired)) {
      dispatch(membershipGoToSignIn());
      dispatch(setCheckoutOverlay(true));
      dispatch(setSkipMembership(false));
      navigate('/member-login');
    }
  }, [isMembershipAvailable, isBannerOrPoster, isSkipLogin, isMembershipRequired, isMemberLoggedIn, isMembershipSkipped]);

  if (!isMembershipAvailable) {
    return null;
  }

  const login = () => {
    const isInDialogModal = !!rootRef.current?.closest('#checkout-overlay');
    if (isBannerOrPoster || isInDialogModal) {
      dispatch(membershipGoToSignIn());
      dispatch(setCheckoutOverlay(true));
      dispatch(setSkipMembership(false));
      navigate('/member-login');
      return;
    }
    dispatch(setSkipMembership(false));
    dispatch(setForceLogin(true));
    dispatch(membershipGoToSignIn());
  };

  const logout = async() => {
    if (loading) {
      return;
    }

    setLoading(true);
    try {
      const res = await wrappedFetch(`/api/memberships/${token}/?update_reservation=true`, {
        method: 'DELETE',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
          'reservation-secret': secret,
        },
      });
      const data = await res.json();
      if (data.deleted) {
        dispatch(setMemberOnlyTicketsDeleted(true));
      }else{
        await dispatch(membershipLogoutAndRefresh(token, secret, isRecurring, selectedChildEvent));
      }
      dispatch(membershipSignOut());
      dispatch(setCheckoutOverlay(false));
      dispatch(setHasMembershipContext(false));
      dispatch(setSkipMembership(false));
    } catch (err) {
      console.error('Logout flow error:', err);
    } finally {
      setLoading(false);
    }
  };

  let linkText = null;
  let onClickHandler = null;

  if (isMemberLoggedIn) {
    linkText = t('checkout__member_logout');
    onClickHandler = logout;
  } else {
    linkText = t('checkout__member_login');
    onClickHandler = login;
  }

  return (
    <div ref={rootRef} className="member-auth-buttons">
      <button
        type="button"
        className={clsx('button button--clear button--medium', isHeroLayout && 'button--full')}
        style={{ textDecoration: 'none' }}
        onClick={(e) => {
          e.preventDefault();
          if (!loading) {
            onClickHandler();
          }
        }}
      >
        {loading ? <><span> <span className='loader'></span> {linkText}</span></> : linkText}
      </button>
    </div>
  );
};

export default MemberAuthButtons;
